import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FriendService } from '../search-friend.service';
import { ConnectedPeopleInterface } from '../user-account';
import { DataSharingService } from '../shared/data-sharing.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-connected-people',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="connected-people-container">
      <div class="section">
        <h3>{{ 'connected-people.RECEIVED_REQUESTS' | translate }}</h3>
        <ul class="user-list">
          <li *ngFor="let user of receivedRequests" class="user-item">
            <span class="user-name">{{ user.name }} {{ user.last_name }}</span>
            <button class="action-btn" (click)="onAcceptFriend(user)">
              {{ 'connected-people.ACCEPT_BUTTON' | translate }}
            </button>
            <button class="action-btn" (click)="onRefuseFriend(user)">
              {{ 'connected-people.REFUSE_BUTTON' | translate }}
            </button>
          </li>
        </ul>
      </div>

      <div class="section">
        <h3>{{ 'connected-people.SENT_REQUESTS' | translate }}</h3>
        <ul class="user-list">
          <li *ngFor="let user of sentRequests" class="user-item">
            <span class="user-name">{{ user.name }} {{ user.last_name }}</span>
          </li>
        </ul>
      </div>

      <div class="section">
        <h3>{{ 'connected-people.FRIENDS' | translate }}</h3>
        <ul class="user-list">
          <li *ngFor="let user of friends" class="user-item">
            <span class="user-name">{{ user.name }} {{ user.last_name }}</span>
            <button
              class="action-btn"
              (click)="onRemoveConnection(user, 'friend')"
            >
              {{ 'connected-people.REMOVE_BUTTON' | translate }}
            </button>
          </li>
        </ul>
      </div>

      <div class="section">
        <h3>{{ 'connected-people.FOLLOWING' | translate }}</h3>
        <ul class="user-list">
          <li *ngFor="let user of following" class="user-item">
            <span class="user-name">{{ user.name }} {{ user.last_name }}</span>
            <button
              class="action-btn"
              (click)="onRemoveConnection(user, 'following')"
            >
              {{ 'connected-people.UNFOLLOW_BUTTON' | translate }}
            </button>
          </li>
        </ul>
      </div>

      <div class="section">
        <h3>{{ 'connected-people.FOLLOWERS' | translate }}</h3>
        <ul class="user-list">
          <li *ngFor="let user of followers" class="user-item">
            <span class="user-name">{{ user.name }} {{ user.last_name }}</span>
            <button
              class="action-btn"
              (click)="onRemoveConnection(user, 'follower')"
            >
              {{ 'connected-people.REMOVE_BUTTON' | translate }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  `,
  styleUrls: ['./connected-people.component.css'],
  providers: [FriendService],
})
export class ConnectedPeopleComponent implements OnInit {
  receivedRequests: ConnectedPeopleInterface[] = []; // Status 1: Pending friend requests (received)
  sentRequests: ConnectedPeopleInterface[] = []; // Status 6: Friend requests sent by the current user
  friends: ConnectedPeopleInterface[] = []; // Status 2: Friends
  followers: ConnectedPeopleInterface[] = []; // Status 4: Followers
  following: ConnectedPeopleInterface[] = []; // Status 3: Following

  constructor(
    private friendService: FriendService,
    private dataSharingService: DataSharingService,
  ) {}

  ngOnInit(): void {
    // Send 0 as status to get all connected people
    this.friendService.getConnectedPeople(0).subscribe((data) => {
      this.categorizeConnections(data);
    });
  }

  // Categorize based on status
  categorizeConnections(data: ConnectedPeopleInterface[]): void {
    this.dataSharingService
      .getActiveUserAccount()
      .subscribe((currentAccount) => {
        data.forEach((person) => {
          switch (person.status) {
            case 1:
              if (person.receiver_id === currentAccount.id) {
                this.receivedRequests.push(person); // Status 1: Received friend requests (pending)
              }
              break;
            case 2:
              if (person.sender_id !== currentAccount.id) {
                this.friends.push(person); // Status 2: Friends
              }
              break;
            case 3:
              if (person.receiver_id === currentAccount.id) {
                this.followers.push(person); // Status 4: Followers
              }
              break;
            case 4:
              if (person.receiver_id === currentAccount.id) {
                this.following.push(person);
              }
              break;
            case 6:
              if (person.receiver_id === currentAccount.id) {
                this.sentRequests.push(person); // Status 6: Friend requests sent by the current user
              }
              break;
          }
        });
      });
  }

  onAcceptFriend(user: ConnectedPeopleInterface): void {
    this.friendService.acceptFriend(user.user_id).subscribe({
      next: () => {
        this.receivedRequests = this.receivedRequests.filter(
          (u) => u.user_id !== user.user_id,
        );
        this.friends.push(user); // Move the user to the friends list after accepting
      },
      error: (error) =>
        console.error('Failed to accept friend request:', error),
    });
  }

  onRefuseFriend(user: ConnectedPeopleInterface): void {
    this.friendService.refuseFriend(user.user_id).subscribe({
      next: () => {
        this.receivedRequests = this.receivedRequests.filter(
          (u) => u.user_id !== user.user_id,
        ); // Remove from receivedRequests
      },
      error: (error) =>
        console.error('Failed to refuse friend request:', error),
    });
  }

  onRemoveConnection(
    user: ConnectedPeopleInterface,
    relationshipType: string,
  ): void {
    this.friendService.removeConnection(user.user_id).subscribe({
      next: () => {
        switch (relationshipType) {
          case 'receivedRequest':
            this.receivedRequests = this.receivedRequests.filter(
              (u) => u.user_id !== user.user_id,
            );
            break;
          case 'sentRequest':
            this.sentRequests = this.sentRequests.filter(
              (u) => u.user_id !== user.user_id,
            );
            break;
          case 'friend':
            this.friends = this.friends.filter(
              (u) => u.user_id !== user.user_id,
            );
            break;
          case 'follower':
            this.followers = this.followers.filter(
              (u) => u.user_id !== user.user_id,
            );
            break;
          case 'following':
            this.following = this.following.filter(
              (u) => u.user_id !== user.user_id,
            );
            break;
        }
      },
      error: (error) => console.error('Failed to remove connection:', error),
    });
  }
}
